import { type FC, type KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import LabelIcon from '@mui/icons-material/Label';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import { Box, CircularProgress, InputBase, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { getLabelColor } from '../../pastelPalette';
import { useGetLabelGroupsQuery, useGetLabelsQuery } from '../../store/api';
import type { Label } from '../../types';

/** Catalog manager — practices create/edit/color/group their labels here. */
const LABELS_ROUTE = '/practice/appointment-labels';

export interface LabelPickerProps {
  organizationId: string;
  /** Currently-selected label IDs. */
  value: string[];
  /** Called with the new full set of selected label IDs. */
  onChange: (labelIds: string[]) => void;
  /** Focus the search field on mount — on in the popover editor, off inline in forms. */
  autoFocus?: boolean;
  /**
   * Opt-in analytics. When set, applying/removing a label emits a Statsig event;
   * `scope` selects the event (appointment vs. appointment-type defaults) and
   * `surface` tags where it fired.
   */
  trackingContext?: {
    scope: 'appointment' | 'appointment_type';
    surface: string;
  };
}

const matchesQuery = (name: string, query: string): boolean =>
  name.toLowerCase().includes(query.trim().toLowerCase());

const SectionHeader: FC<{ title: string; hint?: string }> = ({ title, hint }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      px: 1.5,
      pt: 1,
      pb: 0.25,
    }}
  >
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 700,
        color: 'text.secondary',
        textTransform: 'uppercase',
        letterSpacing: 0.4,
      }}
    >
      {title}
    </Typography>
    {hint && <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>{hint}</Typography>}
  </Box>
);

const ManageLabelsLink: FC<{ onNavigate?: () => void }> = ({ onNavigate }) => (
  <Box
    component={RouterLink}
    to={LABELS_ROUTE}
    onClick={onNavigate}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      px: 1.5,
      py: 0.75,
      fontSize: 13,
      color: 'primary.main',
      textDecoration: 'none',
      borderTop: '1px solid',
      borderColor: 'divider',
      '&:hover': { bgcolor: 'action.hover' },
    }}
  >
    <SettingsOutlined sx={{ fontSize: 16 }} />
    Manage labels
  </Box>
);

/**
 * Searchable command-menu label picker (GDN-2019). One unified menu: grouped
 * labels are single-select (picking a second in a group swaps the first);
 * standalone labels are multi-select. Type to filter, ↑/↓ + Enter to pick. New
 * labels are created in the catalog manager (linked at the bottom) rather than
 * inline, so they can be named, colored, and grouped properly. Emits the full
 * `label_ids` set.
 */
export const LabelPicker: FC<LabelPickerProps> = ({
  organizationId,
  value,
  onChange,
  autoFocus = false,
  trackingContext,
}) => {
  const orgArg = { orgId: organizationId };
  const { data: groupsData, isLoading: groupsLoading } = useGetLabelGroupsQuery(orgArg);
  const { data: labelsData, isLoading: labelsLoading } = useGetLabelsQuery(orgArg);
  const { appointmentLabels: tracking } = useEventTracking();

  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);

  const labels: Label[] = useMemo(() => labelsData?.items ?? [], [labelsData]);
  const groups = useMemo(() => groupsData?.items ?? [], [groupsData]);
  const labelsById = useMemo(
    () => new Map(labels.map(label => [label.label_id, label])),
    [labels],
  );
  const selected = useMemo(() => new Set(value), [value]);

  const visibleGroups = useMemo(
    () =>
      groups
        .map(group => ({
          group,
          labels: labels.filter(
            label => label.label_group_id === group.label_group_id && matchesQuery(label.name, query),
          ),
        }))
        .filter(section => section.labels.length > 0),
    [groups, labels, query],
  );
  const visibleStandalone = useMemo(
    () => labels.filter(label => !label.label_group_id && matchesQuery(label.name, query)),
    [labels, query],
  );

  // Flat list of navigable labels in display order, so ↑/↓ + Enter map to an index.
  const flatLabels = useMemo(
    () => [...visibleGroups.flatMap(section => section.labels), ...visibleStandalone],
    [visibleGroups, visibleStandalone],
  );
  const navIndexById = useMemo(() => {
    const map = new Map<string, number>();
    flatLabels.forEach((label, index) => map.set(label.label_id, index));
    return map;
  }, [flatLabels]);
  const navCount = flatLabels.length;

  useEffect(() => {
    // Keep the highlight in range as filtering changes the visible set.
    setHighlight(current => (navCount === 0 ? 0 : Math.min(current, navCount - 1)));
  }, [navCount]);

  const trackToggle = (
    action: 'added' | 'removed',
    isGrouped: boolean,
    replacedGroupLabel: boolean,
  ) => {
    if (!trackingContext) return;
    if (trackingContext.scope === 'appointment_type') {
      tracking.typeDefaultLabelsChanged({
        action,
        is_grouped: isGrouped,
        ...(replacedGroupLabel && { replaced_group_label: true }),
      });
    } else {
      tracking.appointmentLabelsChanged({
        action,
        source_surface: trackingContext.surface,
        is_grouped: isGrouped,
        ...(replacedGroupLabel && { replaced_group_label: true }),
      });
    }
  };

  const toggleLabel = (label: Label) => {
    if (label.label_group_id) {
      if (selected.has(label.label_id)) {
        onChange(value.filter(id => id !== label.label_id));
        trackToggle('removed', true, false);
      } else {
        const replacedGroupLabel = value.some(
          id => labelsById.get(id)?.label_group_id === label.label_group_id,
        );
        const next = value.filter(
          id => labelsById.get(id)?.label_group_id !== label.label_group_id,
        );
        next.push(label.label_id);
        onChange(next);
        trackToggle('added', true, replacedGroupLabel);
      }
    } else if (selected.has(label.label_id)) {
      onChange(value.filter(id => id !== label.label_id));
      trackToggle('removed', false, false);
    } else {
      onChange([...value, label.label_id]);
      trackToggle('added', false, false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (navCount === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlight(current => (current + 1) % navCount);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight(current => (current - 1 + navCount) % navCount);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      // highlight can fall out of range (e.g. filtering hid the row); guard
      // against flatLabels[-1] / undefined before toggling.
      const label = flatLabels[highlight];
      if (label) toggleLabel(label);
    }
  };

  if ((groupsLoading || labelsLoading) && groups.length === 0 && labels.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1.5, minWidth: 0 }}>
        <CircularProgress size={16} />
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
          Loading labels…
        </Typography>
      </Box>
    );
  }

  if (groups.length === 0 && labels.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Typography sx={{ px: 1.5, pt: 1.5, pb: 1, fontSize: 13, color: 'text.secondary' }}>
          No labels yet.
        </Typography>
        <ManageLabelsLink
          onNavigate={() => tracking.catalogOpened({ source: 'picker_link' })}
        />
      </Box>
    );
  }

  const renderLabelRow = (label: Label) => {
    const index = navIndexById.get(label.label_id) ?? -1;
    const isSelected = selected.has(label.label_id);
    return (
      <Box
        key={label.label_id}
        role="option"
        aria-selected={isSelected}
        onMouseEnter={() => setHighlight(index)}
        onClick={() => toggleLabel(label)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.75,
          borderRadius: 1,
          cursor: 'pointer',
          userSelect: 'none',
          bgcolor: index === highlight ? 'action.hover' : 'transparent',
        }}
      >
        <LabelIcon sx={{ fontSize: 16, color: getLabelColor(label.color), flexShrink: 0 }} />
        <Box
          component="span"
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: 13,
          }}
        >
          {label.name}
        </Box>
        {isSelected && (
          <CheckIcon sx={{ fontSize: 16, color: 'primary.main', flexShrink: 0 }} />
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }} onKeyDown={handleKeyDown}>
      <InputBase
        autoFocus={autoFocus}
        placeholder="Search labels…"
        value={query}
        onChange={event => {
          setQuery(event.target.value);
          setHighlight(0);
        }}
        sx={{
          px: 1.5,
          py: 0.75,
          fontSize: 13,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      />
      <Box role="listbox" aria-label="Labels" sx={{ maxHeight: 280, overflowY: 'auto', p: 0.5 }}>
        {visibleGroups.map(({ group, labels: groupLabels }) => (
          <Box key={group.label_group_id}>
            <SectionHeader title={group.name} hint="Pick one" />
            {groupLabels.map(renderLabelRow)}
          </Box>
        ))}
        {visibleStandalone.length > 0 && (
          <Box>
            <SectionHeader title="Labels" />
            {visibleStandalone.map(renderLabelRow)}
          </Box>
        )}
        {flatLabels.length === 0 && (
          <Typography sx={{ px: 1.5, py: 1, fontSize: 13, color: 'text.secondary' }}>
            No labels match “{query.trim()}”
          </Typography>
        )}
      </Box>
      <ManageLabelsLink
        onNavigate={() => tracking.catalogOpened({ source: 'picker_link' })}
      />
    </Box>
  );
};
